import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import SafeIcon from "../common/SafeIcon";
import * as FiIcons from "react-icons/fi";

const { FiArrowLeft, FiCalendar } = FiIcons;

const ArticleGeneseEnjeux = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <section className="pt-52 pb-20 bg-white min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <Link
            to="/ressources"
            className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
          >
            <SafeIcon icon={FiArrowLeft} className="h-4 w-4 mr-2" />
            Retour aux ressources
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Genèse et enjeux du titre RNCP37275 dédié à la formation professionnelle
          </h1>
          <div className="flex items-center text-gray-500 text-sm">
            <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-2" />
            <span>Mis en ligne le 04/03/2026</span>
          </div>
        </motion.div>

        {/* Article content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="prose prose-lg max-w-none"
        >
          <div className="space-y-8">
            <p className="text-lg text-gray-700 leading-relaxed">
              Dans un paysage de la formation professionnelle en pleine mutation, certains titres émergent comme des repères structurants pour les organismes, les formateurs et les financeurs. Le titre RNCP37275 – « Formateur Professionnel d'Adultes » – s'inscrit dans cette dynamique. Mais d'où vient-il réellement, et pourquoi a-t-il pris une telle importance dans l'écosystème français de la formation ?
            </p>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Un métier longtemps sans cadre formel
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Historiquement, le métier de formateur d'adultes s'est construit de manière empirique. Longtemps, il reposait davantage sur l'expertise métier que sur une véritable reconnaissance formelle des compétences pédagogiques. Or, avec la professionnalisation progressive du secteur et la montée en puissance des exigences qualité (notamment avec Qualiopi), la nécessité d'un référentiel clair s'est imposée.
                </p>
                <p>
                  C'est dans ce contexte qu'a été structuré le titre RNCP37275, inscrit au Répertoire National des Certifications Professionnelles. Son objectif : formaliser un socle de compétences communes, garantir un niveau de qualité homogène, et permettre une meilleure lisibilité pour les employeurs comme pour les apprenants.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Les grandes réformes comme catalyseur
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  La naissance du RNCP37275 ne peut être dissociée des grandes réformes de la formation professionnelle, en particulier celles de 2014 et 2018. Ces réformes ont profondément transformé le secteur, en mettant l'accent sur l'individualisation des parcours, la certification des compétences et le financement via des dispositifs comme le CPF.
                </p>
                <p>
                  Dans ce nouveau cadre, seules les formations certifiantes inscrites au RNCP ou au Répertoire Spécifique peuvent être financées de manière significative. Le titre RNCP37275 s'est donc imposé comme une réponse directe à cette exigence réglementaire.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Un référentiel de compétences ambitieux
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Le contenu du titre reflète cette ambition de professionnalisation. Il s'articule autour de blocs de compétences clés : concevoir et préparer une action de formation, animer des séances en présentiel et à distance, accompagner les apprenants et évaluer les acquis et adapter les dispositifs.
                </p>
                <p>
                  Ce référentiel traduit une évolution majeure : le formateur n'est plus seulement un expert qui transmet, mais un véritable ingénieur pédagogique capable de concevoir des parcours complets, adaptés aux publics et aux contextes.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Un levier stratégique pour les organismes
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Au-delà de sa dimension pédagogique, le RNCP37275 est devenu un levier stratégique pour les organismes de formation. Il permet notamment d'accéder aux financements publics (CPF, Pôle emploi, régions), de renforcer la crédibilité commerciale et de structurer une offre certifiante.
                </p>
                <p>
                  Pour de nombreux organismes, intégrer ce titre dans leur catalogue représente un changement de modèle économique : passage d'une logique de prestations ponctuelles à une logique de parcours certifiants, plus longs et mieux valorisés.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Une diffusion rapide et des perspectives d'avenir
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Depuis son inscription, le RNCP37275 connaît une diffusion rapide. Universités, organismes privés, cabinets de conseil ou structures spécialisées s'en sont emparés. Cette dynamique s'explique par plusieurs facteurs :
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>la demande croissante de reconversion vers les métiers de la formation,</li>
                  <li>le développement du e-learning et des dispositifs hybrides,</li>
                  <li>et la montée en compétence exigée des intervenants.</li>
                </ul>
                <p>
                  Le titre est ainsi devenu une porte d'entrée privilégiée pour celles et ceux qui souhaitent professionnaliser leur activité de formateur ou structurer une nouvelle offre.
                </p>
              </div>
            </div>
          </div>

          {/* Back to resources */}
          <div className="text-center pt-12">
            <Link
              to="/ressources"
              className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
            >
              <SafeIcon icon={FiArrowLeft} className="h-4 w-4 mr-2" />
              Retour aux ressources
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ArticleGeneseEnjeux;
